import { useEffect, useState } from "react";
import api from "../lib/typedApi";
import { PageHeader, NBCard, NBInput, NBSelect, NBButton, NBTextarea, EmptyState } from "../components/UI";
import { toast } from "sonner";
import { Plus, Trash2, X, Siren, Phone, AlertTriangle, Send, Bell } from "lucide-react";

const TYPES = ["contact", "medical", "info"];

export default function Emergency() {
  const [items, setItems] = useState([]);
  const [members, setMembers] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [open, setOpen] = useState(false);
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertSending, setAlertSending] = useState(false);
  const [alertForm, setAlertForm] = useState({ subject: "", message: "" });
  const [form, setForm] = useState({ type: "contact", name: "", phone: "", notes: "", member_id: "" });

  const load = async () => {
    const [e, m, a] = await Promise.all([api.get("/api/emergency"), api.get("/api/members"), api.get("/api/emergency/alerts")]);
    setItems(e.data);
    setMembers(m.data);
    setAlerts(a.data);
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/api/emergency", form);
      toast.success("Saved!");
      setOpen(false);
      setForm({ type: "contact", name: "", phone: "", notes: "", member_id: "" });
      load();
    } catch { toast.error("Failed"); }
  };

  const sendAlert = async (e) => {
    e.preventDefault();
    if (!alertForm.subject.trim() || !alertForm.message.trim()) return;
    setAlertSending(true);
    try {
      const { data } = await api.post("/api/emergency/alert", {
        ...alertForm,
        app_url: window.location.origin,
      });
      const total = (data.email_sent || 0) + (data.sms_sent || 0);
      if (total > 0) {
        const parts = [];
        if (data.email_sent) parts.push(`${data.email_sent} email`);
        if (data.sms_sent) parts.push(`${data.sms_sent} SMS`);
        toast.success(`SOS alert sent: ${parts.join(" + ")}!`);
        setAlertOpen(false);
        setAlertForm({ subject: "", message: "" });
        load();
      } else {
        toast.error(data.skipped || data.error || "No recipients (members need email or phone)");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send");
    }
    setAlertSending(false);
  };

  const remove = async (id) => { await api.delete(`/api/emergency/${id}`); load(); };

  const typeColor = { contact: "#E9B5A0", medical: "#B8CFA9", info: "#E8B96B" };
  const memberById = (id) => members.find((m) => m.id === id);

  return (
    <div data-testid="emergency-page">
      <PageHeader
        title="Emergency Info"
        subtitle="Critical contacts and medical info — accessible when seconds matter."
        color="#E9B5A0"
        action={
          <div className="flex gap-2 flex-wrap">
            <NBButton testId="send-sos-btn" color="#E9B5A0" onClick={() => setAlertOpen(true)}>
              <Bell strokeWidth={3} className="w-4 h-4 inline mr-1" /> SOS Alert
            </NBButton>
            <NBButton testId="add-emergency-btn" color="white" onClick={() => setOpen(true)}>
              <Plus strokeWidth={3} className="w-4 h-4 inline mr-1" />Add Info
            </NBButton>
          </div>
        }
      />

      <NBCard color="#E9B5A0" className="mb-6" hover={false}>
        <div className="flex items-start gap-3">
          <AlertTriangle strokeWidth={3} className="shrink-0 mt-0.5" />
          <div>
            <h3 className="font-black text-lg" style={{ fontFamily: "var(--hh-display)" }}>In an emergency, dial 911 (US)</h3>
            <p className="text-sm">Below are saved contacts and medical info for your family.</p>
          </div>
        </div>
      </NBCard>

      {items.length === 0 ? (
        <EmptyState title="No emergency info yet" subtitle="Add emergency contacts, medical info, allergies, etc." />
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((it) => {
            const m = memberById(it.member_id);
            return (
              <NBCard key={it.id} color={typeColor[it.type]} data-testid={`emergency-${it.id}`}>
                <div className="flex justify-between">
                  <span className="nb-badge bg-white capitalize">{it.type}</span>
                  <button onClick={() => remove(it.id)} className="nb-btn bg-white p-1.5">
                    <Trash2 strokeWidth={2.5} className="w-4 h-4" />
                  </button>
                </div>
                <h4 className="text-lg font-black mt-2" style={{ fontFamily: "var(--hh-display)" }}>{it.name}</h4>
                {it.phone && <a href={`tel:${it.phone}`} className="text-sm font-bold mt-1 flex items-center gap-1 underline"><Phone strokeWidth={2.5} className="w-3.5 h-3.5" /> {it.phone}</a>}
                {m && <p className="text-xs font-bold mt-1">For: {m.name}</p>}
                {it.notes && <p className="text-sm mt-2 whitespace-pre-wrap">{it.notes}</p>}
              </NBCard>
            );
          })}
        </div>
      )}

      {open && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div className="nb-card max-w-md w-full p-6" onClick={(e) => e.stopPropagation()} style={{ boxShadow: "8px 8px 0 0 rgba(0,0,0,1)" }}>
            <div className="flex justify-between mb-4">
              <h2 className="text-2xl font-black" style={{ fontFamily: "var(--hh-display)" }}>Emergency Info</h2>
              <button onClick={() => setOpen(false)} className="nb-btn bg-[#E9B5A0] p-1.5"><X strokeWidth={3} className="w-4 h-4" /></button>
            </div>
            <form onSubmit={submit} className="space-y-3">
              <NBSelect testId="emergency-type" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}>
                {TYPES.map((t) => <option key={t}>{t}</option>)}
              </NBSelect>
              <NBInput testId="emergency-name" required placeholder="Name / Title" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              <NBInput testId="emergency-phone" placeholder="Phone (if applicable)" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
              <NBSelect testId="emergency-member" value={form.member_id} onChange={(e) => setForm({ ...form, member_id: e.target.value })}>
                <option value="">For whole family</option>
                {members.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
              </NBSelect>
              <NBTextarea testId="emergency-notes" placeholder="Notes (allergies, medications, conditions, etc)" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
              <NBButton testId="submit-emergency" type="submit" color="#E9B5A0" className="w-full">Save</NBButton>
            </form>
          </div>
        </div>
      )}

      {alertOpen && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setAlertOpen(false)}>
          <div className="nb-card max-w-md w-full p-6" onClick={(e) => e.stopPropagation()} style={{ boxShadow: "8px 8px 0 0 #E9B5A0", border: "3px solid #0A0A0A" }}>
            <div className="flex justify-between mb-4">
              <div>
                <span className="nb-badge bg-black text-white">⚠ SOS</span>
                <h2 className="text-2xl font-black mt-2" style={{ fontFamily: "var(--hh-display)" }}>Send Family Alert</h2>
              </div>
              <button onClick={() => setAlertOpen(false)} className="nb-btn bg-white p-1.5"><X strokeWidth={3} className="w-4 h-4" /></button>
            </div>
            <p className="text-sm text-neutral-700 mb-3">This will <strong>email and SMS every family member</strong> with contact info on file. Use only for real emergencies.</p>
            <form onSubmit={sendAlert} className="space-y-3">
              <NBInput testId="alert-subject" required maxLength={80} placeholder="What's happening? (short)" value={alertForm.subject} onChange={(e) => setAlertForm({ ...alertForm, subject: e.target.value })} />
              <NBTextarea testId="alert-message" required maxLength={500} placeholder="Details: where, what's needed, who to contact..." value={alertForm.message} onChange={(e) => setAlertForm({ ...alertForm, message: e.target.value })} className="min-h-[120px]" />
              <NBButton testId="submit-alert" type="submit" disabled={alertSending} color="#E9B5A0" className="w-full">
                <Send strokeWidth={3} className={`w-4 h-4 inline mr-1 ${alertSending ? "animate-pulse" : ""}`} />
                {alertSending ? "Sending..." : "Send SOS to Family"}
              </NBButton>
            </form>
            <p className="text-xs text-neutral-500 mt-3 text-center">SMS requires phone numbers in <code className="bg-neutral-100 px-1">+15551234567</code> format on member profiles. Email always sent if available.</p>
          </div>
        </div>
      )}

      {alerts.length > 0 && (
        <div className="mt-8">
          <h3 className="text-2xl font-black mb-4" style={{ fontFamily: "var(--hh-display)" }}>Recent Alerts</h3>
          <div className="space-y-2">
            {alerts.slice(0, 5).map((a) => (
              <div key={a.id} className="nb-card !p-3" data-testid={`alert-${a.id}`}>
                <div className="flex items-start gap-3">
                  <Bell strokeWidth={3} className="text-rose-700 mt-0.5 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="font-bold">{a.subject}</p>
                    <p className="text-sm text-neutral-600 line-clamp-2">{a.message}</p>
                    <p className="text-xs text-neutral-500 mt-1">{new Date(a.created_at).toLocaleString()} · {(a.email_sent ?? 0)} email · {(a.sms_sent ?? 0)} SMS</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
